import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, PlayCircle, UploadCloud, Eye, Building2 } from "lucide-react";

const STATUS_LABELS = {
  completata: { label: "Completata", cls: "bg-green-100 text-green-800" },
  non_completata: { label: "Non completata", cls: "bg-red-100 text-red-800" },
  annullata: { label: "Annullata", cls: "bg-red-100 text-red-800" },
};
const DEFAULT_STATUS = { label: "Da completare", cls: "bg-amber-100 text-amber-800" };

const DOC_LABELS = {
  titolo_autodichiarazione: "Autodichiarazione titolo",
  titolo_diploma: "Diploma",
  titolo_dichiarazione_valore: "Dichiarazione di valore",
  carta_identita: "Carta d'identità",
  codice_fiscale_doc: "Copia codice fiscale",
  curriculum: "Curriculum vitae",
  lettera_motivazionale: "Lettera motivazionale",
  cert_inglese_b1: "Certificazione inglese (B1 o superiore)",
  cert_ecdl: "Certificazione ECDL/ICDL",
};

function ApplicationCard({ app, session }) {
  const navigate = useNavigate();
  const status = STATUS_LABELS[app.status] || DEFAULT_STATUS;
  const activeSession = session && ["section_a_active", "section_b_active"].includes(session.status);
  const canStart = app.status === "inviata" || activeSession;

  const startTest = () => {
    if (session) localStorage.setItem("its_test_session", session.id);
    navigate(`/test/consenso?app=${app.id}`);
  };

  const viewOutcome = () => {
    if (session) localStorage.setItem("its_test_session", session.id);
    navigate("/test/completamento");
  };

  return (
    <Card className="institutional-card fade-up" data-testid={`application-card-${app.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900 mt-1">{app.course_name}</h2>
          </div>
          <Badge data-testid={`application-status-${app.id}`} className={status.cls}>{status.label}</Badge>
        </div>
        <dl className="mt-4 space-y-1.5 text-sm">
          <div className="flex justify-between"><dt className="text-slate-500">Codice fiscale</dt><dd className="font-mono2 text-slate-900">{app.anagrafica.codice_fiscale}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-500">Inviata il</dt><dd className="text-slate-900">{new Date(app.created_at).toLocaleDateString("it-IT")}</dd></div>
        </dl>
        <div className="mt-4 flex flex-wrap gap-2" data-testid={`documents-list-${app.id}`}>
          {Object.keys(app.documents || {}).map((k) => (
            <span key={k} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 rounded-full px-2.5 py-1">
              <FileText className="h-3 w-3" /> {DOC_LABELS[k] || k}
            </span>
          ))}
        </div>
        <div className="mt-5">
          {canStart && (
            <Button data-testid={`start-test-${app.id}`} size="sm" className="bg-[#E05A47] hover:bg-[#c94836]" onClick={startTest}>
              <PlayCircle className="h-4 w-4 mr-2" /> {activeSession ? "Riprendi il test" : "Avvia il test proctored"}
            </Button>
          )}
          {session && ["completed", "screened_out", "terminated"].includes(session.status) && (
            <Button data-testid={`view-outcome-${app.id}`} size="sm" variant="outline" onClick={viewOutcome}>
              <Eye className="h-4 w-4 mr-2" /> Stato della selezione
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState(null);
  const [sessions, setSessions] = useState({});
  const [pendingTenant, setPendingTenant] = useState(null);

  useEffect(() => {
    const pending = sessionStorage.getItem("pending_update_token");
    if (pending) {
      sessionStorage.removeItem("pending_update_token");
      navigate(`/aggiornamento/${pending}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/applications/mine");
        setApplications(data);
        const pendingSlug = localStorage.getItem("its_tenant_slug");
        if (pendingSlug) {
          const t = await api.get(`/tenants/slug/${pendingSlug}`).then((r) => r.data).catch(() => null);
          if (t && t.active && t.applications_open && !data.some((a) => a.tenant_id === t.id)) setPendingTenant(t);
        }
        const sessMap = {};
        await Promise.all(data.map(async (a) => {
          try {
            const { data: s } = await api.get(`/tests/by-application/${a.id}`);
            sessMap[a.id] = s;
          } catch {
            // 404 atteso: la candidatura non ha ancora una sessione di test
          }
        }));
        setSessions(sessMap);
      } catch (e) {
        console.error("Caricamento candidature fallito", e);
        setApplications([]);
      }
    };
    load();
  }, []);

  const byTenant = new Map();
  (applications || []).forEach((a) => {
    if (!byTenant.has(a.tenant_id)) byTenant.set(a.tenant_id, { id: a.tenant_id, name: a.tenant_name, apps: [] });
    byTenant.get(a.tenant_id).apps.push(a);
  });
  const groups = [...byTenant.values()];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="candidate-dashboard">
        <div className="fade-up">
          <p className="eyebrow mb-2">Area riservata candidato</p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Benvenuto, {user?.name}
          </h1>
        </div>

        {pendingTenant && (
          <div data-testid="pending-tenant-cta"
            className="mt-8 institutional-card rounded-xl border-2 border-[#003366]/25 bg-blue-50/60 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <Building2 className="h-8 w-8 text-[#003366] shrink-0" />
              <div>
                <p className="font-display font-semibold text-slate-900">{pendingTenant.name}</p>
                <p className="text-sm text-slate-600">
                  Le candidature per questo istituto sono aperte: puoi presentare la tua domanda ora.
                </p>
              </div>
            </div>
            <Button data-testid="pending-tenant-apply" className="bg-[#003366] hover:bg-[#002244]" onClick={() => navigate("/candidatura")}>
              Inizia la candidatura
            </Button>
          </div>
        )}

        {applications === null ? (
          <p className="mt-8 text-slate-500 text-sm">Caricamento...</p>
        ) : applications.length === 0 ? (
          <Card className="institutional-card mt-8 fade-up">
            <CardContent className="p-8 sm:p-12 text-center">
              <UploadCloud className="h-12 w-12 text-[#003366] mx-auto mb-4" />
              <h2 className="font-display text-xl font-semibold text-slate-900">Nessuna candidatura presente</h2>
              <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
                Scegli l'istituto ITS e il corso, carica i documenti richiesti e invia la domanda di ammissione.
              </p>
              <p data-testid="dashboard-no-application-hint" className="mt-6 text-sm text-slate-600">
                Per presentare una candidatura utilizza il link o il QR code fornito dall'istituto ITS di tuo interesse.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="mt-8 space-y-10" data-testid="applications-by-tenant">
            {groups.map((g) => (
              <section key={g.id} data-testid={`tenant-group-${g.id}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="h-4 w-4 text-[#003366]" />
                  <h2 className="eyebrow">{g.name}</h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {g.apps.map((a) => (
                    <ApplicationCard key={a.id} app={a} session={sessions[a.id]} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
        <p className="mt-8 text-xs text-slate-400">
          Gli esiti delle prove non sono mostrati in quest'area: al termine della fase di selezione riceverai una
          comunicazione dall'istituto.
        </p>
      </div>
    </div>
  );
}
